/**
 * Channel Settings Service
 * Contains channel settings operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../services/useApi';
import {
  ChannelRate,
  ChannelSettingsResponse,
  UpdateChannelRateRequest,
} from '../types';

// ============================================
// API Endpoints
// ============================================

const API_ENDPOINTS = {
  GET_CHANNEL_SETTINGS: '/api/DistributionManagement/GetChannelSettings',
  UPDATE_CHANNEL_RATE: '/api/DistributionManagement/UpdateChannelRate',
  UPDATE_WEEKDAYS_PRICE: '/api/Calender/UpdateWeekDaysPrice',
} as const;

// ============================================
// Types
// ============================================

interface IUpdateWeekDaysPriceRequest {
  dateFrom: string;
  dateTo: string;
  propertyIds: number[];
  price: number;
  weekDays: number[];
}

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch channel settings
 * @returns Query result with channel settings data
 */
export const useChannelSettingsQuery = () => {
  const { apiPrivate } = useApi();

  const fetchChannelSettings = async (): Promise<ChannelSettingsResponse> => {
    const response = await apiPrivate.get<ChannelSettingsResponse>(
      API_ENDPOINTS.GET_CHANNEL_SETTINGS
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to fetch channel settings'
      );
    }

    return response.data;
  };

  return useQuery({
    queryKey: ['channel-settings'],
    queryFn: fetchChannelSettings,
    staleTime: 0,
    retry: 2,
  });
};

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to update channel rate
 * @returns Mutation for updating channel rate
 */
export const useUpdateChannelRateMutation = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const updateChannelRate = async (request: UpdateChannelRateRequest): Promise<void> => {
    const response = await apiPrivate.post(
      API_ENDPOINTS.UPDATE_CHANNEL_RATE,
      request
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to update channel rate'
      );
    }
  };

  return useMutation({
    mutationFn: updateChannelRate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-settings'] });
    },
  });
};

/**
 * Hook to update weekdays price
 * @returns Mutation for updating weekdays price
 */
export const useUpdateWeekDaysPriceMutation = () => {
  const { apiPrivate } = useApi();

  const updateWeekDaysPrice = async (request: IUpdateWeekDaysPriceRequest): Promise<void> => {
    const response = await apiPrivate.post(
      API_ENDPOINTS.UPDATE_WEEKDAYS_PRICE,
      request
    );

    return response.data;
  };

  return useMutation({
    mutationFn: updateWeekDaysPrice,
  });
};
